import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getRoundByShortCode, respondToJoinRequest, confirmRoundTime } from '../services/roundService';
import ShareRoundModal from '../components/ShareRoundModal';
import { trackEvent } from '../services/analytics';
import { formatRoundDate, formatRoundTimeWithViewerNote, roundStartInstant } from '../utils/timeHelpers';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRoundPermissions } from '../hooks/useRoundPermissions';
import WeatherBadge from '../components/WeatherBadge';
import RoundWeatherSection from '../components/RoundWeatherSection';
import BookingStatusSection from '../components/BookingStatusSection';
import BookingStatusBadge from '../components/BookingStatusBadge';
import EditRoundModal from '../components/EditRoundModal';
import RoundResponseModal from '../components/RoundResponseModal';
import InvitePlayersModal from '../components/InvitePlayersModal';
import SafeIcon from '../common/SafeIcon';
import { getInitials } from '../utils/initials';
import {
  FiEdit3, FiUsers, FiCalendar,
  FiClock, FiMapPin, FiFlag, FiPhone, FiExternalLink,
  FiCheck, FiX, FiArrowRight, FiUserPlus, FiShare2,
} from 'react-icons/fi';

const formatTime = (timeString) => {
  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  } catch {
    return timeString;
  }
};

function RoundDetailPage({ groups }) {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmingSlotId, setConfirmingSlotId] = useState(null);

  const permissions = useRoundPermissions(round, groups);

  const fetchRound = useCallback(async () => {
    if (!shortCode) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await getRoundByShortCode(shortCode);
    if (fetchError) {
      setError('Failed to load round details');
      setLoading(false);
      return;
    }

    if (!data) {
      setRound(null);
      setLoading(false);
      return;
    }

    // Enrich invitations with a name from group_members when the RPC's
    // primary join missed (e.g. friends-style rounds where the round has no
    // group_id but the invitee is still in a shared group with the creator).
    // The RPC also now falls back to auth.users.raw_user_meta_data on the
    // server side, so by the time we reach here non-Unknown is the norm.
    const needsName = (n) => !n || n === 'Unknown' || n === 'Invited player';

    const invitations = Array.isArray(data.invitations) ? [...data.invitations] : [];
    const userIds = [...new Set(invitations.map(inv => inv.user_id).filter(Boolean))];

    if (userIds.length > 0) {
      const { data: members } = await supabase
        .from('group_members')
        .select('user_id, group_id, member_name')
        .in('user_id', userIds);

      if (members) {
        const nameMap = {};
        members.forEach(m => {
          if (!m.member_name) return;
          if (data.group_id && m.group_id === data.group_id) {
            nameMap[m.user_id] = m.member_name;
          } else if (!nameMap[m.user_id]) {
            nameMap[m.user_id] = m.member_name;
          }
        });
        invitations.forEach(inv => {
          if (needsName(inv.member_name) && nameMap[inv.user_id]) {
            inv.member_name = nameMap[inv.user_id];
          }
        });
      }
    }

    // Fallback for the current user (typically the solo round creator): if
    // they're listed but still nameless, use their auth metadata.
    if (user) {
      const selfName = user.user_metadata?.full_name || user.email || 'You';
      invitations.forEach(inv => {
        if (inv.user_id === user.id && needsName(inv.member_name)) {
          inv.member_name = selfName;
        }
      });

      // For solo rounds the creator might not be in invitations at all -
      // inject a synthetic "Accepted" row so they show up in the player list.
      if (!data.group_id && data.created_by === user.id) {
        const alreadyListed = invitations.some(inv => inv.user_id === user.id);
        if (!alreadyListed) {
          invitations.unshift({
            user_id: user.id,
            member_name: selfName,
            status: 'accepted',
          });
        }
      }
    }

    setRound({ ...data, invitations });
    setLoading(false);
  }, [shortCode, user]);

  useEffect(() => {
    fetchRound();
  }, [fetchRound]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <SEO title="Loading Round | TeeSync" noindex={true} />
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 bg-card rounded-lg flex items-center justify-center border border-border-subtle mx-auto mb-6"
          >
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-accent" />
          </motion.div>
          <p className="text-text-secondary font-medium">Loading round details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Error | TeeSync" noindex={true} />
        <div className="text-center">
          <div className="bg-status-error/10 text-status-error rounded-lg p-6 mb-4">
            <p className="font-medium">{error}</p>
          </div>
          <button
            onClick={fetchRound}
            className="text-accent hover:text-interactive-hover font-semibold mr-4"
          >
            Try again
          </button>
          <Link to="/rounds" className="text-text-secondary hover:text-text-primary font-semibold">
            Back to My Rounds
          </Link>
        </div>
      </div>
    );
  }

  // 404 state
  if (!round) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Round Not Found | TeeSync" noindex={true} />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary">Round not found</h1>
          <p className="text-text-secondary mt-2">
            This round may have been deleted or you don't have access to view it.
          </p>
          <Link
            to="/rounds"
            className="mt-4 inline-block text-accent hover:text-interactive-hover font-semibold"
          >
            Return to My Rounds
          </Link>
        </div>
      </div>
    );
  }

  // Determine round status by the round's actual start instant. Comparing
  // raw round.date against local midnight misclassifies cross-zone rounds
  // at the day boundary (e.g. a UK round shown to an LA viewer near LA's
  // local midnight).
  const _isUpcomingInstant = roundStartInstant(round);
  const isUpcoming = _isUpcomingInstant ? _isUpcomingInstant.getTime() >= Date.now() : false;
  const isCreator = user?.id === round.created_by;

  // Parse invitations from JSONB
  const invitations = round.invitations || [];
  const userInvitation = invitations.find(inv => inv.user_id === user?.id) || null;
  const userStatus = userInvitation?.status ?? null;
  const isUserPending = userStatus === 'pending';
  // An answered invitation can still be changed, up to the round's start
  // instant. 'requested' is excluded: that one is the host's call.
  const canChangeResponse = isUpcoming && (userStatus === 'accepted' || userStatus === 'declined');
  const canRespond = isUpcoming && (isUserPending || canChangeResponse);
  const joinRequests = invitations.filter(inv => inv.status === 'requested');

  const handleJoinRequest = async (inv, approve) => {
    if (!inv?.id) return;
    await respondToJoinRequest(inv.id, approve);
    await fetchRound();
  };

  const showBookingSection = round.course && round.course !== 'To be determined';
  const showBookingChip = isUpcoming && showBookingSection;

  // Proposed-slot scheduling: until the creator confirms a time, the round's
  // date/time hold the earliest proposed slot as a placeholder.
  const isProposed = round.scheduling_status === 'proposed';
  const proposedSlots = Array.isArray(round.proposed_slots) ? round.proposed_slots : [];
  const slotResponders = (slotId) =>
    invitations.filter(
      (inv) => inv.status === 'accepted' && Array.isArray(inv.slot_ids) && inv.slot_ids.includes(slotId)
    );

  const handleConfirmSlot = async (slotId) => {
    if (confirmingSlotId) return;
    setConfirmingSlotId(slotId);
    const { error: confirmError } = await confirmRoundTime(round.id, slotId);
    setConfirmingSlotId(null);
    if (!confirmError) {
      trackEvent('round_time_confirmed', { short_code: round.short_code, slot_id: slotId });
      await fetchRound();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <SEO
        title={`${round.course || 'Round'} - ${formatRoundDate(round, { day: 'numeric', month: 'short', year: undefined, weekday: undefined })} | TeeSync`}
        noindex={true}
      />

      {/* Top row: date/time + action icons on mobile; on desktop becomes a
          two-column layout with booking sidebar on the right. */}
      <div className="space-y-6">
        {/* Header: date/time + action icons */}
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                <span className="hidden sm:inline">
                  {formatRoundDate(round, { weekday: 'long', day: 'numeric', month: 'long', year: undefined })}
                </span>
                <span className="sm:hidden">
                  {formatRoundDate(round, { weekday: 'short', day: 'numeric', month: 'short', year: undefined })}
                </span>
              </h1>
              {(() => {
                const tz = round.time
                  ? formatRoundTimeWithViewerNote(round, { hour12: true })
                  : { primary: 'Time TBD', secondary: null };
                return (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div className="flex flex-col">
                      <p className="text-base font-semibold text-accent inline-flex items-center">
                        <SafeIcon icon={FiClock} className="w-4 h-4 mr-1.5" />
                        {tz.primary}
                      </p>
                      {tz.secondary && (
                        <p className="text-xs text-text-secondary ml-5">{tz.secondary}</p>
                      )}
                    </div>
                    {isUpcoming && round.weather_golf_score && (
                      <WeatherBadge
                        golfScore={round.weather_golf_score}
                        golfCategory={round.weather_golf_category}
                        compact
                        compactSize="md"
                      />
                    )}
                    {showBookingChip && (
                      <BookingStatusBadge
                        isBooked={round.is_booked || false}
                        variant="inline"
                        size="md"
                      />
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isUpcoming && (
                <button
                  type="button"
                  onClick={() => setShowShareModal(true)}
                  aria-label="Share round"
                  className="w-11 h-11 bg-surface hover:bg-card rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <SafeIcon icon={FiShare2} className="w-4 h-4 text-text-secondary" />
                </button>
              )}
              {permissions.canEdit && (
                <button
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  aria-label="Edit round"
                  className="w-11 h-11 bg-surface hover:bg-card rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <SafeIcon icon={FiEdit3} className="w-4 h-4 text-text-secondary" />
                </button>
              )}
            </div>
          </div>

          {/* Course summary row - same compact metadata pattern as GroupDetails */}
          {(round.course || round.hole_count) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-secondary">
              {round.course && (
                <span className="inline-flex items-center min-w-0">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1.5 text-text-muted flex-shrink-0" />
                  <span className="font-medium text-text-primary truncate">{round.course}</span>
                </span>
              )}
              <span className="inline-flex items-center">
                <SafeIcon icon={FiFlag} className="w-4 h-4 mr-1.5 text-text-muted" />
                {round.hole_count || 18} Holes
              </span>
              {round.course_phone && (
                <a
                  href={`tel:${round.course_phone}`}
                  className="inline-flex items-center text-text-muted hover:text-accent transition-colors"
                >
                  <SafeIcon icon={FiPhone} className="w-4 h-4 mr-1.5" />
                  {round.course_phone}
                </a>
              )}
              {round.course_website && !round.golfnow_booking_url && (
                <a
                  href={round.course_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-text-muted hover:text-accent transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-1.5" />
                  Website
                </a>
              )}
            </div>
          )}

          {/* Inline link to group/friends + Respond CTA. The Link uses
              inline-flex and the button is inline-block by default, so they
              were sharing a line. Wrap in flex-col so they each take their
              own row with a clear gap. */}
          {((round.group_id && round.group_invite_code) || round.round_type === 'friends' || canRespond || userStatus) && (
            <div className="flex flex-col items-start gap-3">
              {round.group_id && round.group_invite_code ? (
                <Link
                  to={`/group/${round.group_invite_code}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-interactive-hover transition-colors"
                >
                  View group
                  <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
                </Link>
              ) : round.round_type === 'friends' ? (
                <Link
                  to="/friends"
                  className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-interactive-hover transition-colors"
                >
                  View friends
                  <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
                </Link>
              ) : null}

              {/* Respond CTA. Answered invitations get a change affordance
                  instead of nothing, plus a line stating the answer on record.
                  Once the round has started the status line stays but the
                  button goes, matching the server-side cutoff. */}
              {canChangeResponse && (
                <p className="text-sm text-text-secondary">
                  You {userStatus === 'accepted' ? 'accepted' : 'declined'} this round.
                </p>
              )}

              {canRespond && (
                <button
                  type="button"
                  onClick={() => setShowResponseModal(true)}
                  className={`w-full sm:w-auto py-3 px-4 rounded-lg transition-colors duration-200 text-sm font-semibold ${
                    canChangeResponse
                      ? 'border border-border-subtle text-text-secondary hover:bg-surface'
                      : 'bg-accent text-white hover:bg-interactive-hover'
                  }`}
                >
                  {canChangeResponse ? 'Change response' : 'Respond to Invitation'}
                </button>
              )}

              {!isUpcoming && userStatus && userStatus !== 'requested' && (
                <p className="text-sm text-text-muted">
                  You {userStatus === 'accepted' ? 'accepted' : userStatus === 'declined' ? 'declined' : 'did not respond to'} this round.
                </p>
              )}
            </div>
          )}

        {/* Booking status - full-width on both mobile and desktop. */}
        {showBookingSection && (
          <BookingStatusSection
            round={round}
            canToggle={isCreator}
            onBookingChange={() => fetchRound()}
          />
        )}
      </div>

      {/* Join Requests (creator-only) */}
      {isCreator && round.is_open_invite && joinRequests.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-text-primary mb-3 flex items-center">
            <SafeIcon icon={FiUserPlus} className="w-5 h-5 mr-2 text-text-muted" />
            Join requests ({joinRequests.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {joinRequests.map((inv) => (
              <div
                key={inv.id || inv.user_id}
                className="flex items-center justify-between p-3 rounded-lg bg-card border border-border-subtle"
              >
                <div className="flex items-center min-w-0">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="font-semibold text-sm text-accent">
                      {getInitials(inv.member_name)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {inv.member_name || 'A player'}
                    </p>
                    <p className="text-xs text-text-secondary">wants to join</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleJoinRequest(inv, false)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-surface text-text-secondary hover:text-text-primary border border-border-subtle"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => handleJoinRequest(inv, true)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-accent text-white hover:bg-interactive-hover"
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proposed time slots - visible until the creator confirms one */}
      {isProposed && proposedSlots.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-text-primary mb-1 flex items-center">
            <SafeIcon icon={FiClock} className="w-5 h-5 mr-2 text-text-muted" />
            Proposed tee times
          </h3>
          <p className="text-sm text-text-secondary mb-3">
            {isCreator
              ? 'Players are picking the times they can make. Confirm one to lock it in.'
              : 'The organiser will confirm one of these times.'}
          </p>
          <div className="space-y-2">
            {proposedSlots.map((slot) => {
              const responders = slotResponders(slot.id);
              return (
                <div
                  key={slot.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-card border border-border-subtle"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary">
                      {new Date(`${slot.slot_date}T00:00`).toLocaleDateString('en-GB', {
                        weekday: 'short', day: 'numeric', month: 'short',
                      })}
                      {' · '}
                      {formatTime(slot.slot_time)}
                    </p>
                    <p className="text-xs text-text-secondary truncate">
                      {responders.length === 0
                        ? 'No responses yet'
                        : `${responders.length} can make it: ${responders
                            .map((inv) => inv.member_name || 'Guest')
                            .join(', ')}`}
                    </p>
                  </div>
                  {isCreator && (
                    <button
                      type="button"
                      onClick={() => handleConfirmSlot(slot.id)}
                      disabled={!!confirmingSlotId}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold bg-accent text-white hover:bg-interactive-hover disabled:opacity-50 flex-shrink-0"
                    >
                      {confirmingSlotId === slot.id ? 'Confirming...' : 'Confirm time'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Player List */}
      <div>
        <h3 className="text-base font-semibold text-text-primary mb-3 flex items-center">
          <SafeIcon icon={FiUsers} className="w-5 h-5 mr-2 text-text-muted" />
          Players ({invitations.length})
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {invitations.map((invitation, index) => {
            const statusConfig = {
              accepted: { pill: 'bg-status-success/10 text-status-success', icon: FiCheck, label: 'Accepted' },
              pending: { pill: 'bg-status-warning/10 text-status-warning', icon: FiClock, label: 'Pending' },
              declined: { pill: 'bg-status-error/10 text-status-error', icon: FiX, label: 'Declined' },
            };
            const config = statusConfig[invitation.status] || statusConfig.pending;

            return (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-card border border-border-subtle"
              >
                <div className="flex items-center min-w-0">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="font-semibold text-sm text-accent">
                      {getInitials(invitation.member_name)}
                    </span>
                  </div>
                  <span className="font-medium text-text-primary truncate">
                    {invitation.member_name && invitation.member_name !== 'Unknown'
                      ? invitation.member_name
                      : 'Invited player'}
                  </span>
                  {invitation.is_guest && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-subtle text-text-muted flex-shrink-0">
                      Guest
                    </span>
                  )}
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium flex-shrink-0 ${config.pill}`}
                >
                  <SafeIcon icon={config.icon} className="w-3.5 h-3.5" />
                  {config.label}
                </span>
              </div>
            );
          })}

          {invitations.length === 0 && (
            <p className="text-sm text-text-muted text-center py-4">No players invited</p>
          )}

          {isUpcoming && isCreator && invitations.length < 4 && (
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-app border border-dashed border-border-subtle hover:border-accent transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiUserPlus} className="w-4 h-4 text-accent" />
              </div>
              <span className="font-medium text-text-secondary">
                {invitations.length < 3 ? 'Invite players' : 'Invite a player'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Weather / Conditions Section */}
      {isUpcoming && (
        <RoundWeatherSection round={round} onRefreshed={fetchRound} />
      )}

      {/* Modals */}
      <EditRoundModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        round={round}
        onUpdateSuccess={async () => {
          setShowEditModal(false);
          await fetchRound();
        }}
        onDeleteSuccess={() => navigate('/rounds')}
      />

      <RoundResponseModal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        round={round}
        currentStatus={userStatus}
        onResponseSuccess={async () => {
          setShowResponseModal(false);
          await fetchRound();
        }}
      />

      <ShareRoundModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        round={round}
        organiserName={user?.user_metadata?.full_name}
        onToggled={() => fetchRound()}
      />

      <InvitePlayersModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        round={round}
        onInviteSuccess={async () => {
          setShowInviteModal(false);
          await fetchRound();
        }}
      />

    </div>
  );
}

export default RoundDetailPage;
